import { createContext } from 'react';
import { compact, uniq } from 'lodash';
import { hashQueryKey, QueryClient, useQueries } from '@tanstack/react-query';
import { queryClient as appQueryClient } from 'app/queryClient';
import createBatchLookup, { BatchLookupOptions } from 'app/util/lookups/createBatchLookup';

export type LookupId = string | number | null | undefined;

export interface LookupResult<TData> {
  /** undefined while loading, null if not found */
  data: TData | null | undefined;
  isLoading: boolean;
  error: unknown;
}

export interface LookupManyResult<TData> {
  /** undefined until all loaded; then found records only, in input order */
  data: TData[] | undefined;
  isLoading: boolean;
  error: unknown;
}

export interface LookupOptions<TData> extends BatchLookupOptions {
  /** matches fetched records to requested ids; defaults to record.id */
  getKey?: (record: TData) => string | number;
  /** runs on records from a fetch or ingest; keep it sync and cheap */
  onReceive?: (records: TData[]) => void;
  staleTime?: number;
  cacheTime?: number;
  queryClient?: QueryClient;
}

export const hasId = (id: LookupId): id is string | number => id != null && String(id) !== '';

export const uniqueIds = (ids: LookupId[]) => uniq(ids.filter(hasId).map(String));

export const lookupQueryKey = (name: string, id: string | number) => [name, String(id)] as const;

/** batched lookup, cached per record by key; hooks for components, imperative API for everything else */
export default function createLookup<TData extends { id: string | number }>(
  name: string,
  fetchBatch: (ids: string[]) => Promise<TData[]>,
  {
    getKey = (record) => record.id,
    onReceive,
    batchSize,
    batchDelay,
    staleTime = 30000,
    cacheTime,
    queryClient = appQueryClient
  }: LookupOptions<TData> = {}
) {
  const batchLoader = createBatchLookup(
    (ids) =>
      fetchBatch(ids).then((records) => {
        onReceive?.(records);
        return records;
      }),
    getKey,
    { batchSize, batchDelay }
  );

  if (cacheTime !== undefined) {
    queryClient.setQueryDefaults([name], { cacheTime });
  }

  // works outside QueryClientProvider (e.g. popovers)
  const context = createContext<QueryClient | undefined>(queryClient);

  const queryKey = (id: string | number) => lookupQueryKey(name, id);

  const queryOptions = (id: string | number) => ({
    queryKey: queryKey(id),
    queryFn: () => batchLoader.load(id),
    staleTime,
    retry: 1 // only failed requests; missing records resolve null
  });

  const useLookupMany = (ids: LookupId[]): LookupManyResult<TData> => {
    const results = useQueries({ queries: uniqueIds(ids).map((id) => queryOptions(id)), context });
    const isLoaded = results.every(({ data }) => data !== undefined);

    return {
      data: isLoaded ? compact(results.map(({ data }) => data as TData | null)) : undefined,
      isLoading: results.some(({ isLoading }) => isLoading),
      error: results.find(({ error }) => error)?.error ?? null
    };
  };

  const useLookup = (id: LookupId): LookupResult<TData> => {
    const { data, ...result } = useLookupMany([id]);

    if (!hasId(id) || !data) {
      return { ...result, data: undefined };
    }

    return { ...result, data: data[0] ?? null };
  };

  /** fetches if uncached or stale; calls are batched */
  const lookup = (id: LookupId): Promise<TData | null> =>
    hasId(id) ? queryClient.fetchQuery(queryOptions(id)) : Promise.resolve(null);

  /** found records only, deduped, in input order */
  const lookupMany = (ids: LookupId[]): Promise<TData[]> => Promise.all(uniqueIds(ids).map(lookup)).then(compact);

  /** cache read, never fetches: undefined if not loaded, null if not found */
  const get = (id: LookupId): TData | null | undefined =>
    hasId(id)
      ? (queryClient.getQueryCache().get(hashQueryKey(queryKey(id)))?.state.data as TData | null | undefined)
      : undefined;

  /** seeds records fetched elsewhere; skips cached ones so late responses can't overwrite them */
  const ingest = (records: TData[]) => {
    records.forEach((record) => {
      const key = getKey(record);

      if (get(key) === undefined) {
        queryClient.setQueryData(queryKey(key), record);
      }
    });

    onReceive?.(records);
  };

  /** marks records stale (all if no ids); they refetch on next use */
  const invalidate = (ids?: LookupId | LookupId[]) => {
    if (!Array.isArray(ids)) {
      return queryClient.invalidateQueries({ queryKey: hasId(ids) ? queryKey(ids) : [name] });
    }

    // one cache pass, not one per id
    const keys = new Set(ids.filter(hasId).map(String));
    return queryClient.invalidateQueries({
      queryKey: [name],
      predicate: ({ queryKey: [, id] }) => keys.has(String(id))
    });
  };

  /** drops all records; rendered ones refetch */
  const clear = () => queryClient.resetQueries({ queryKey: [name] });

  /** cancels fetches and drops all records without refetching (e.g. logout) */
  const remove = () => {
    queryClient.cancelQueries({ queryKey: [name] });
    queryClient.removeQueries({ queryKey: [name] });
  };

  return { useLookup, useLookupMany, lookup, lookupMany, get, ingest, invalidate, clear, remove, queryKey };
}
